import { EmeraldErrorModal } from 'src/common/api/emerald-error';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { TenderType } from 'src/common/enumerations/tender-type';
import { IReceiptArgs } from 'src/common/interfaces/dtos/IReceiptArgs';
import { getPaybackSellableTenders } from 'src/home-page/business-logic/service/get-payback-sellable-tenders';
import { handleGiftCardRefund } from 'src/returns/business-logic/handle-gift-card-refund';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { ITender } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { makeTenderPayment } from '../../home-page/business-logic/make-tender-payment';
import { preMakeTenderPayment } from '../../home-page/business-logic/pre-make-tender-payment';
import { popupAsyncMessage } from '../../messages/business-logic/popup-message';
import { closeEftTenderingView } from '../components/process-tender/tender-processing-view';
import { buildTenderAddDTO } from './build-line-tender';
import { addRefundEFTTender, showTenderingAndGetCardDetails } from './handle-eft-tendering';
import { handleSwitchRefundTenders, showRefundModal } from './handle-refund-actions';
import { AddTenderProps } from './handle-tender-payment';

export const refundPayment = async (tender: ITender, isSwitchTender?: boolean): Promise<void> => {
  return processTenderRefund(tender).catch(async err => {
    await popupAsyncMessage(err);
    const refundTenderGroup = await getPaybackSellableTenders();
    if (isSwitchTender) {
      return handleSwitchRefundTenders(refundTenderGroup);
    } else {
      showRefundModal(refundTenderGroup);
    }
  });
};

const processTenderRefund = async (tender: ITender): Promise<void> => {
  const usage = RefundUsage.Refund;
  const amountDue = store.getState().itemList.amountDue;

  // TODO revisit when we get real recommended tenders response
  const isGiftCard = tender.tenderReferences?.some(
    r => ['EPS_GiftCard', 'GC', 'GC GiftCards'].indexOf(r) >= 0,
  );
  const isEwic = tender.tenderReferences?.some(r => ['Ewic', 'EW', 'EWeWic'].indexOf(r) >= 0);

  const tenderProps: AddTenderProps = {
    tender,
    amount: amountDue,
    amountDue,
    lineItemEntryMethod: EntryMethod.Automatic,
    usage,
  };

  if (isGiftCard) {
    const lineTender = buildTenderAddDTO(tenderProps);
    await preMakeTenderPayment(lineTender);
    return handleGiftCardRefund();
  }

  if (tender.type === TenderType.Cash) {
    return handleCashReturn(tender);
  }

  if (isEwic) {
    return handleEwicRefundProhibited();
  }

  return showTenderingAndGetCardDetails(tenderProps)
    .then(() => addRefundEFTTender(tenderProps))
    .finally(closeEftTenderingView);
};

const handleCashReturn = async (tender: ITender): Promise<void> => {
  //build DTO
  const tenderAddDTO = buildTenderAddDTO({
    tender: tender,
    amount: tender.amount ?? 0,
    lineItemEntryMethod: EntryMethod.Automatic,
    typeCode: RefundUsage.Refund,
  });

  await preMakeTenderPayment(tenderAddDTO);

  const { transactionCustomer: customer } = store.getState().transaction;

  const receiptsArgs: IReceiptArgs = {
    printReceipt: true,
    emailAddress: customer?.emailAddress,
    mobilePhoneNumber: customer?.mobilePhoneNumber,
  };

  return makeTenderPayment(tenderAddDTO, receiptsArgs.printReceipt, receiptsArgs);
};

const handleEwicRefundProhibited = async (): Promise<void> => {
  throw new EmeraldErrorModal({
    message: 'RefundNotificationTitle',
    subMessage: 'RefundEwicNotificationProhibited',
    type: EmeraldDialogTypes.Info,
  });
};
